/*
==================================================
ADDRESS STEP

Функция:
полный автономный шаг адреса внутри mobile cart modal

Родитель:
cart-mobile-body.tsx

Дочерние элементы:
SignInPrompt
ShippingAddress
BillingAddress
ErrorMessage

Использует:
локальный draft state
валидацию
submit в Medusa
controller bridge для footer
==================================================
*/

"use client";

import { useActionState, useEffect, useMemo, useRef, useState } from "react";
import { HttpTypes } from "@medusajs/types";
import { useToggleState } from "@medusajs/ui";
import ShippingAddress from "@modules/checkout/components/shipping-address";
import BillingAddress from "@modules/checkout/components/billing_address";
import SignInPrompt from "@modules/cart/components/sign-in-prompt";
import ErrorMessage from "@modules/checkout/components/error-message";
import { setAddressesForMobileModal } from "@lib/data/cart";

type DraftAddressData = Record<string, string>;

type AddressStepProps = {
  cart?: HttpTypes.StoreCart | null;
  customer?: HttpTypes.StoreCustomer | null;
  onRegisterController: (controller: { submit: () => void; reset?: () => void } | null) => void;
  onSubmittingChange: (value: boolean) => void;
  onSaved: () => void;
};

function buildDraftAddressData(cart?: HttpTypes.StoreCart | null): DraftAddressData {
  return {
    "shipping_address.first_name": cart?.shipping_address?.first_name || "",
    "shipping_address.last_name": cart?.shipping_address?.last_name || "",
    "shipping_address.address_1": cart?.shipping_address?.address_1 || "",
    "shipping_address.company": cart?.shipping_address?.company || "",
    "shipping_address.postal_code": cart?.shipping_address?.postal_code || "",
    "shipping_address.city": cart?.shipping_address?.city || "",
    "shipping_address.country_code": cart?.shipping_address?.country_code || "",
    "shipping_address.province": cart?.shipping_address?.province || "",
    "shipping_address.phone": cart?.shipping_address?.phone || "",
    email: cart?.email || "",
  };
}

export default function AddressStep({
  cart,
  customer = null,
  onRegisterController,
  onSubmittingChange,
  onSaved,
}: AddressStepProps) {
  const [draftAddressData, setDraftAddressData] = useState<DraftAddressData>(
    buildDraftAddressData(cart)
  );
  const [addressError, setAddressError] = useState("");
  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);

  const addressCardRef = useRef<HTMLDivElement>(null);
  const addressFormRef = useRef<HTMLFormElement>(null);

  const [addressSaveMessage, formAction] = useActionState(
    setAddressesForMobileModal as any,
    null
  );

  const { state: sameAsBilling, toggle: toggleSameAsBilling } = useToggleState(
    cart?.shipping_address && cart?.billing_address
      ? JSON.stringify(cart.shipping_address) === JSON.stringify(cart.billing_address)
      : true
  );

  useEffect(() => {
    setDraftAddressData(buildDraftAddressData(cart));
    setAddressError("");
    setIsSubmittingAddress(false);
    onSubmittingChange(false);
  }, [cart, onSubmittingChange]);

  const modalCart = useMemo(() => {
    if (!cart) return cart;

    return {
      ...cart,
      email: draftAddressData.email || cart.email,
      shipping_address: {
        ...cart.shipping_address,
        first_name: draftAddressData["shipping_address.first_name"] || "",
        last_name: draftAddressData["shipping_address.last_name"] || "",
        address_1: draftAddressData["shipping_address.address_1"] || "",
        company: draftAddressData["shipping_address.company"] || "",
        postal_code: draftAddressData["shipping_address.postal_code"] || "",
        city: draftAddressData["shipping_address.city"] || "",
        country_code: draftAddressData["shipping_address.country_code"] || "",
        province: draftAddressData["shipping_address.province"] || "",
        phone: draftAddressData["shipping_address.phone"] || "",
      },
    };
  }, [cart, draftAddressData]);

  function getAddressFieldValue(name: string) {
    return draftAddressData[name]?.trim() || "";
  }

  function handleAddressDraftChange(e: React.FormEvent<HTMLDivElement>) {
    const target = e.target as HTMLInputElement | HTMLSelectElement | null;

    if (!target?.name) return;

    setDraftAddressData((prev) => ({
      ...prev,
      [target.name]: target.value,
    }));

    if (addressError) {
      setAddressError("");
    }
  }

  function validateAddressStep() {
    const firstName = getAddressFieldValue("shipping_address.first_name");
    const lastName = getAddressFieldValue("shipping_address.last_name");
    const address1 = getAddressFieldValue("shipping_address.address_1");
    const postalCode = getAddressFieldValue("shipping_address.postal_code");
    const city = getAddressFieldValue("shipping_address.city");
    const countryCode = getAddressFieldValue("shipping_address.country_code");
    const email = getAddressFieldValue("email");
    const phone = getAddressFieldValue("shipping_address.phone");

    if (!firstName) return "Enter first name.";
    if (!lastName) return "Enter last name.";
    if (!address1) return "Enter address.";
    if (!postalCode) return "Enter postal code.";
    if (!city) return "Enter city.";
    if (!countryCode) return "Select country.";
    if (!email) return "Enter email address.";
    if (!phone) return "Enter phone number.";

    return "";
  }

  function scrollToAddressError() {
    requestAnimationFrame(() => {
      if (addressCardRef.current) {
        addressCardRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    });
  }

  function submitAddressToMedusa() {
    if (!addressFormRef.current) return;

    const error = validateAddressStep();

    if (error) {
      setAddressError(error);
      scrollToAddressError();
      return;
    }

    setAddressError("");
    setIsSubmittingAddress(true);
    onSubmittingChange(true);
    addressFormRef.current.requestSubmit();
  }

  function resetAddressStep() {
    setDraftAddressData(buildDraftAddressData(cart));
    setAddressError("");
    setIsSubmittingAddress(false);
    onSubmittingChange(false);
  }

  useEffect(() => {
    onRegisterController({
      submit: submitAddressToMedusa,
      reset: resetAddressStep,
    });

    return () => {
      onRegisterController(null);
    };
  }, [onRegisterController, cart, draftAddressData, addressError]);

  useEffect(() => {
    if (!isSubmittingAddress) return;

    if (addressSaveMessage) {
      setIsSubmittingAddress(false);
      onSubmittingChange(false);
      scrollToAddressError();
      return;
    }

    const savedFirstName = cart?.shipping_address?.first_name?.trim() || "";
    const savedLastName = cart?.shipping_address?.last_name?.trim() || "";
    const savedAddress1 = cart?.shipping_address?.address_1?.trim() || "";
    const savedPostalCode = cart?.shipping_address?.postal_code?.trim() || "";
    const savedCity = cart?.shipping_address?.city?.trim() || "";
    const savedCountryCode = cart?.shipping_address?.country_code?.trim() || "";
    const savedPhone = cart?.shipping_address?.phone?.trim() || "";
    const savedEmail = cart?.email?.trim() || "";

    const draftFirstName = draftAddressData["shipping_address.first_name"]?.trim() || "";
    const draftLastName = draftAddressData["shipping_address.last_name"]?.trim() || "";
    const draftAddress1 = draftAddressData["shipping_address.address_1"]?.trim() || "";
    const draftPostalCode = draftAddressData["shipping_address.postal_code"]?.trim() || "";
    const draftCity = draftAddressData["shipping_address.city"]?.trim() || "";
    const draftCountryCode = draftAddressData["shipping_address.country_code"]?.trim() || "";
    const draftPhone = draftAddressData["shipping_address.phone"]?.trim() || "";
    const draftEmail = draftAddressData.email?.trim() || "";

    const savedMatchesDraft =
      savedFirstName === draftFirstName &&
      savedLastName === draftLastName &&
      savedAddress1 === draftAddress1 &&
      savedPostalCode === draftPostalCode &&
      savedCity === draftCity &&
      savedCountryCode === draftCountryCode &&
      savedPhone === draftPhone &&
      savedEmail === draftEmail;

    if (savedMatchesDraft) {
      setIsSubmittingAddress(false);
      onSubmittingChange(false);
      setAddressError("");
      onSaved();
    }
  }, [
    isSubmittingAddress,
    addressSaveMessage,
    cart,
    draftAddressData,
    onSaved,
    onSubmittingChange,
  ]);

  return (
    <div className="rounded-t-2xl border-x border-t border-slate-200 bg-white px-4 py-6 shadow-[0_4px_12px_rgba(15,23,42,0.2)]">
      <form ref={addressFormRef} action={formAction} className="mobile-modal-address-container">
        <div className="mb-5 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white px-4 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
          <SignInPrompt />
        </div>

        <div
          ref={addressCardRef}
          onInputCapture={handleAddressDraftChange}
          onChangeCapture={handleAddressDraftChange}
          className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/70 px-3 py-0 shadow-[0_10px_24px_rgba(15,23,42,0.08)]"
        >
          <div className="text-[1.05rem] font-extrabold tracking-[0.04em] text-slate-900">
            Shipping address
          </div>

          {addressError && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 shadow-sm">
              {addressError}
            </div>
          )}

          {!addressError && addressSaveMessage && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 shadow-sm">
              <ErrorMessage error={addressSaveMessage} data-testid="modal-address-error-message" />
            </div>
          )}

          <div className="mt-5">
            <ShippingAddress
              customer={customer}
              checked={sameAsBilling}
              onChange={toggleSameAsBilling}
              cart={modalCart as any}
            />
          </div>

          {!sameAsBilling && (
            <div className="mt-2">
              <div className="pb-4 pt-2 text-[1rem] font-extrabold tracking-[0.03em] text-slate-900">
                Billing address
              </div>
              <BillingAddress cart={modalCart as any} />
            </div>
          )}
        </div>

        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1}>
          Save address
        </button>
      </form>
    </div>
  );
}
